import { lazy, Suspense } from 'react'
import Layout from '@/components/Layout'
import GlobalAlerts from '@/components/GlobalAlerts'
// HomePage stays eager: it is the LCP route, so lazying it only adds a
// request waterfall in front of the largest paint. Every other page is
// split out of the entry chunk.
import HomePage from '@/pages/HomePage'

const LoginPage = lazy(() => import('@/pages/LoginPage'))
const SignUpPage = lazy(() => import('@/pages/SignUpPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const VerifyEmailChangePage = lazy(() => import('@/pages/VerifyEmailChangePage'))
const VerifyAccountPage = lazy(() => import('@/pages/VerifyAccountPage'))
const AccountPage = lazy(() => import('@/pages/AccountPage'))
const AccountSettingsPage = lazy(() => import('@/pages/AccountSettingsPage'))
const OrderDetailsPage = lazy(() => import('@/pages/OrderDetailsPage'))
const ProductPage = lazy(() => import('@/pages/ProductPage'))
const CmsPage = lazy(() => import('@/pages/CmsPage'))
const CategoryPage = lazy(() => import('@/pages/CategoryPage'))
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'))
const PromotionsPage = lazy(() => import('@/pages/PromotionsPage'))
const FeaturedPage = lazy(() => import('@/pages/FeaturedPage'))
const BestSellersPage = lazy(() => import('@/pages/BestSellersPage'))
const StockoffPage = lazy(() => import('@/pages/StockoffPage'))
const SearchPage = lazy(() => import('@/pages/SearchPage'))
const NewsPage = lazy(() => import('@/pages/NewsPage'))
const NewsListPage = lazy(() => import('@/pages/NewsListPage'))
const EventsListPage = lazy(() => import('@/pages/EventsListPage'))
const EventPage = lazy(() => import('@/pages/EventPage'))
const CartPage = lazy(() => import('@/pages/CartPage'))
const ShippingAddressPage = lazy(() => import('@/pages/ShippingAddressPage'))
const PaymentPage = lazy(() => import('@/pages/PaymentPage'))
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage'))
const PaymentFailPage = lazy(() => import('@/pages/PaymentFailPage'))
const OrderConfirmationPage = lazy(() => import('@/pages/OrderConfirmationPage'))
const FavoritesPage = lazy(() => import('@/pages/FavoritesPage'))
const KitPage = lazy(() => import('@/pages/KitPage'))

import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import AuthLayout from '@/layouts/AuthLayout'
import RequireAuth from '@/components/RequireAuth'
import SessionGuard from '@/components/SessionGuard'
import { useAuthStore } from '@/store/authStore'
import { useChannelStore } from '@/store/channel'
import { getPostLoginRedirect } from '@/utils/authRedirect'
import LanguageRoute from '@/components/LanguageRoute'
import LocalizedNavigate from '@/components/LocalizedNavigate'
import { useLocalizedNavigate, useLocalizedPath } from '@/hooks/useLocalizedPath'

function RouteFallback() {
  return <div className="w-full flex justify-center py-12" role="status" aria-live="polite" />
}

// Rendered under `/:lang/*`, so the paths below are relative to the language prefix.
function AuthenticatedApp() {
  const navigate = useLocalizedNavigate()

  return (
    <Layout
      onNavigateToAccount={() => navigate('/account')}
      onNavigateToCart={() => navigate('/cart')}
    >
      <GlobalAlerts />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/"
            element={
              <HomePage onNavigateToCategory={(slug) => navigate(`/categories/${slug}`)} />
            }
          />

          <Route path="/account" element={<RequireAuth />}>
            <Route
              index
              element={
                <AccountPage
                  onNavigateToSettings={() => navigate('/account/settings')}
                  onViewOrderDetails={(orderId) => navigate(`/account/orders/${orderId}`)}
                />
              }
            />
            <Route
              path="settings"
              element={<AccountSettingsPage onBack={() => navigate('/account')} />}
            />
            <Route
              path="orders/:orderId"
              element={<OrderDetailsPageWrapper onBack={() => navigate('/account')} />}
            />
          </Route>

          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/promotions" element={<PromotionsPage />} />
          <Route path="/featured" element={<FeaturedPage />} />
          <Route path="/best-sellers" element={<BestSellersPage />} />
          <Route path="/liquidacao" element={<StockoffPage />} />
          <Route
            path="/categories/:slug"
            element={
              <CategoryPageWrapper
                onNavigateToProduct={(slug) => navigate(`/products/${slug}`)}
              />
            }
          />
          <Route path="/products/:slug" element={<ProductPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/news" element={<NewsListPage />} />
          <Route path="/news/:id" element={<NewsPage />} />
          <Route path="/events" element={<EventsListPage />} />
          <Route path="/events/:slug" element={<EventPage />} />

          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/kits/:slug" element={<KitPage />} />

          <Route
            path="/cart"
            element={
              <CartPage
                onBack={() => navigate('/')}
                onCheckout={() => navigate('/checkout/shipping')}
                onNavigateToLogin={() => navigate('/login')}
              />
            }
          />

          <Route
            path="/checkout/shipping"
            element={
              <ShippingAddressPage
                onBack={() => navigate('/cart')}
                onPlaceOrder={(code) => navigate(`/checkout/confirmation/${code}`)}
              />
            }
          />

          <Route path="/payment/:orderId" element={<PaymentPage />} />
          <Route path="/checkout/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/checkout/payment/error" element={<PaymentFailPage />} />

          <Route
            path="/checkout/confirmation/:code"
            element={<OrderConfirmationPage onContinueShopping={() => navigate('/')} />}
          />

          <Route path="/pages/:id" element={<CmsPage />} />
          <Route path="/:slug" element={<CmsPage />} />

          <Route path="*" element={<LocalizedNavigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

// Wrapper for OrderDetailsPage to extract orderId from params
function OrderDetailsPageWrapper({ onBack }: { onBack: () => void }) {
  const { orderId } = useParams<{ orderId: string }>()
  return <OrderDetailsPage orderId={orderId || ''} onBack={onBack} />
}

// Wrapper for CategoryPage to extract slug from params
function CategoryPageWrapper({
  onNavigateToProduct,
}: {
  onNavigateToProduct: (slug: string) => void
}) {
  const { slug } = useParams<{ slug: string }>()
  return <CategoryPage slug={slug || ''} onNavigateToProduct={onNavigateToProduct} />
}

export default function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const location = useLocation()
  const lp = useLocalizedPath()
  const shopIsPublic = useChannelStore((state) => state.shopIsPublic)
  const canBrowseWithoutAuth = shopIsPublic || isAuthenticated
  const storefront = canBrowseWithoutAuth ? <AuthenticatedApp /> : <Navigate to={lp('/login')} replace />

  return (
    <>
      <SessionGuard />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* `/` has no language segment: LanguageRoute redirects it to `/<lang>`. */}
          <Route path="/" element={<LanguageRoute />} />
          <Route path="/:lang" element={<LanguageRoute />}>
            <Route element={<AuthLayout />}>
              <Route
                path="login"
                element={isAuthenticated ? <Navigate to={lp(getPostLoginRedirect(location.state))} replace /> : <LoginPage />}
              />
              <Route
                path="signup"
                element={isAuthenticated ? <Navigate to={lp('/')} replace /> : <SignUpPage />}
              />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="password-reset" element={<ResetPasswordPage />} />
              <Route path="verify-email-address-change" element={<VerifyEmailChangePage />} />
              <Route path="verify" element={<VerifyAccountPage />} />
            </Route>
            {/* `index` too: on bare `/pt` the router ranks `/:lang` above `/:lang/*`, leaving the Outlet empty. */}
            <Route index element={storefront} />
            <Route path="*" element={storefront} />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}
